import JSZip from 'jszip';
import { getUrl } from '@aws-amplify/storage';
import fileCabinetService from 'services/fileCabinet';
import { generateClient } from 'aws-amplify/api';
import { getCERTIFICATE } from 'graphql/queries';

const API = generateClient();

/**
 * Downloads a file from URL and adds it to the zip
 */
async function addFileToZip(zip, filePath, fileName) {
  try {
    console.log(`Adding file to zip: ${fileName} from path: ${filePath}`);
    
    // Ensure the path has 'protected/' prefix for proper S3 access
    const fullPath = filePath.startsWith('protected/') ? filePath : `protected/${filePath}`;
    
    // Get the download URL for the file - pass path directly, not in options
    const { url } = await getUrl({
      path: fullPath
    });
    
    console.log(`Generated S3 URL for ${fileName}`);
    console.log(`S3 Path used: ${fullPath}`);
    
    // Fetch the file content
    const response = await fetch(url.href);
    if (!response.ok) {
      console.error(`Failed to fetch file: ${fileName}, status: ${response.status}, path: ${fullPath}`);
      console.error(`Full URL attempted: ${url.href.split('?')[0]}`); // Log URL without query params for security
      return false;
    }
    
    const blob = await response.blob();
    const fileSize = blob.size;
    
    if (fileSize === 0) {
      console.warn(`File ${fileName} is empty (0 bytes), skipping`);
      return false;
    }
    
    zip.file(fileName, blob);
    console.log(`Successfully added file: ${fileName} (${(fileSize / 1024).toFixed(2)} KB)`);
    return true;
  } catch (error) {
    console.error(`Error adding file ${fileName} to zip:`, error);
    console.error(`Path attempted: ${filePath}`);
    if (error.message && error.message.includes('NoSuchKey')) {
      console.error(`File not found in S3: ${filePath}`);
    }
    return false;
  }
}

/**
 * Gets certificate details by ID
 */
async function getCertificateDetails(certificateID) {
  try {
    const response = await API.graphql({
      query: getCERTIFICATE,
      variables: { id: certificateID }
    });
    const cert = response.data.getCERTIFICATE;
    if (cert) {
      console.log(`Certificate ${certificateID} details:`, {
        id: cert.id,
        acord25File: cert.acord25File,
        scanId: cert.scanId,
        files: cert.files?.items?.length || 0,
        supplementalFiles: cert.supplementalCoiFiles?.items?.length || 0
      });
    }
    return cert;
  } catch (error) {
    console.error(`Error fetching certificate ${certificateID}:`, error);
    return null;
  }
}

/**
 * Recursively adds folder contents to zip
 */
async function addFolderToZip(zip, folder, basePath = '', clientID) {
  const folderPath = basePath ? `${basePath}/${folder.name}` : folder.name;
  
  console.log(`Processing folder: ${folderPath}, ID: ${folder.id}`);
  
  // Track statistics
  let stats = {
    totalFiles: 0,
    successfulFiles: 0,
    failedFiles: 0
  };
  
  // Get folder contents
  const folderContents = await fileCabinetService.getFolderContents(folder.id);
  
  if (!folderContents) {
    console.error(`Failed to get contents for folder: ${folder.name}`);
    return stats;
  }
  
  console.log('Folder contents response:', folderContents);
  console.log(`Folder ${folder.name} has ${folderContents.folderItems?.items?.length || 0} items`);
  
  // If folder is empty, add a placeholder file to preserve folder structure
  const hasItems = folderContents.folderItems?.items?.length > 0;
  const hasSubFolders = folderContents.subFolders?.items?.length > 0;
  
  if (!hasItems && !hasSubFolders) {
    console.log(`Folder ${folder.name} is empty, adding placeholder`);
    zip.file(`${folderPath}/.empty`, 'This folder is empty');
  }
  
  // Add documents in this folder
  if (folderContents.folderItems && folderContents.folderItems.items) {
    for (const item of folderContents.folderItems.items) {
      console.log(`Processing item: ${item.documentName}, type: ${item.documentType}, certificateID: ${item.certificateID}`);
      
      // Determine item type and handle accordingly
      if (item.documentType === 'document' || item.documentType === 'uploaded_file') {
        // Handle uploaded documents and files
        if (item.certificateID && item.certificateID.startsWith('file_')) {
          const timestamp = item.certificateID.replace('file_', '');
          const fileName = item.documentName;
          const fileExtension = fileName.split('.').pop();
          const baseName = fileName.replace(`.${fileExtension}`, '');
          const fullFileName = `${baseName}_${timestamp}.${fileExtension}`;
          
          // Reconstruct the file path for uploaded files
          const filePath = `protected/file-cabinet/${clientID}/${folder.id}/${fullFileName}`;
          
          console.log(`Attempting to add uploaded file: ${fileName} from path: ${filePath}`);
          stats.totalFiles++;
          const success = await addFileToZip(zip, filePath, `${folderPath}/${fileName}`);
          if (success) {
            stats.successfulFiles++;
          } else {
            stats.failedFiles++;
          }
        } else {
          // Handle documents without file_ prefix - these might be stored differently
          console.log(`Document without file_ prefix: ${item.documentName}`);
          // Skip for now as we don't know the storage pattern
          console.warn(`Skipping document ${item.documentName} - unknown storage pattern`);
        }
        
      } else if (item.documentType === 'certificate') {
        // Handle certificates
        console.log(`Processing certificate: ${item.documentName}, certificateID: ${item.certificateID}`);
        
        if (item.certificateID && !item.certificateID.startsWith('file_')) {
          // Fetch full certificate data
          console.log(`Fetching certificate details for: ${item.certificateID}`);
          const certificate = await getCertificateDetails(item.certificateID);
        
        if (certificate) {
          console.log('Certificate data:', certificate);
          let filesAdded = 0;
          
          // Check for ACORD 25 file
          if (certificate.acord25File) {
            const fileName = item.documentName || certificate.acord25File;
            // Try multiple possible paths for ACORD files
            const pathsToTry = [
              `protected/certificates/${certificate.id}/${certificate.acord25File}`,
              `protected/certificates/${certificate.id}/acord25/${certificate.acord25File}`,
            ];
            
            // If scanning, add scanning paths
            if (certificate.scanId) {
              pathsToTry.push(`protected/scanning/input/${certificate.id}/${certificate.scanId}/${certificate.acord25File}`);
              pathsToTry.push(`protected/scanning/${certificate.id}/${certificate.acord25File}`);
            }
            
            let fileAdded = false;
            stats.totalFiles++;
            
            for (const filePath of pathsToTry) {
              console.log(`Attempting to add ACORD file from path: ${filePath}`);
              const success = await addFileToZip(zip, filePath, `${folderPath}/${fileName}`);
              if (success) {
                filesAdded++;
                stats.successfulFiles++;
                fileAdded = true;
                console.log(`Successfully added ACORD file from: ${filePath}`);
                break;
              }
            }
            
            if (!fileAdded) {
              stats.failedFiles++;
              console.warn(`Failed to find ACORD file in any location for certificate ${certificate.id}`);
            }
          }
          
          // Check for files array
          if (certificate.files && certificate.files.items && certificate.files.items.length > 0) {
            console.log(`Certificate has ${certificate.files.items.length} files`);
            for (const file of certificate.files.items) {
              const fileName = file.name || file.filename || `file_${file.id}`;
              // Use the path directly if provided, otherwise construct it
              let filePath = file.fullPath || file.path || `protected/certificates/${certificate.id}/${file.name}`;
              console.log(`Adding file: ${fileName} from path: ${filePath}`);
              stats.totalFiles++;
              const success = await addFileToZip(zip, filePath, `${folderPath}/${fileName}`);
              if (success) {
                filesAdded++;
                stats.successfulFiles++;
              } else {
                stats.failedFiles++;
              }
            }
          }
          
          // Check for supplemental COI files
          if (certificate.supplementalCoiFiles && certificate.supplementalCoiFiles.items && certificate.supplementalCoiFiles.items.length > 0) {
            console.log(`Certificate has ${certificate.supplementalCoiFiles.items.length} supplemental files`);
            for (const file of certificate.supplementalCoiFiles.items) {
              const fileName = file.name || file.filename || `supplemental_${file.id}`;
              // Supplemental files use a specific path structure
              const insuranceType = file.insuranceType || 'unknown';
              const documentType = file.documentType || 'unknown';
              let filePath = file.fullPath || file.path || `protected/certificates/${certificate.id}/SupplementalCoverageDocumentation/${insuranceType}/${documentType}/${file.name}`;
              console.log(`Adding supplemental file: ${fileName} from path: ${filePath}`);
              stats.totalFiles++;
              const success = await addFileToZip(zip, filePath, `${folderPath}/${fileName}`);
              if (success) {
                filesAdded++;
                stats.successfulFiles++;
              } else {
                stats.failedFiles++;
              }
            }
          }
          
          // If no files were added, try constructing a default path for the certificate PDF
          if (filesAdded === 0 && item.documentName) {
            const fileName = item.documentName;
            // Try common certificate storage patterns based on the correct S3 structure
            const pathsToTry = [
              `protected/certificates/${certificate.id}/${fileName}`,
              `protected/certificates/${certificate.id}/acord25.pdf`,
              `protected/certificates/${certificate.id}/${certificate.id}.pdf`,
              `protected/certificates/${certificate.id}/acord25/${fileName}`,
            ];
            
            // If we have a scanId, also try scanning paths
            if (certificate.scanId) {
              pathsToTry.push(`protected/scanning/input/${certificate.id}/${certificate.scanId}/${fileName}`);
              pathsToTry.push(`protected/scanning/${certificate.id}/${fileName}`);
            }
            
            for (const path of pathsToTry) {
              console.log(`Trying fallback path: ${path}`);
              stats.totalFiles++;
              const success = await addFileToZip(zip, path, `${folderPath}/${fileName}`);
              if (success) {
                filesAdded++;
                stats.successfulFiles++;
                break;
              } else {
                stats.failedFiles++;
              }
            }
            
            if (filesAdded === 0) {
              console.warn(`Could not find any files for certificate ${certificate.id} with name ${fileName}`);
            }
          }
        } else {
          console.error(`Failed to fetch certificate: ${item.certificateID}`);
          // Still try to download the file using the documentName
          if (item.documentName) {
            console.log(`Attempting to download certificate file directly: ${item.documentName}`);
            const pathsToTry = [
              `protected/certificates/${item.certificateID}/${item.documentName}`,
              `protected/file-cabinet/${clientID}/${folder.id}/${item.documentName}`,
              `protected/certificates/${item.certificateID}/${item.certificateID}.pdf`
            ];
            
            stats.totalFiles++;
            let fileAdded = false;
            
            for (const path of pathsToTry) {
              console.log(`Trying path: ${path}`);
              const success = await addFileToZip(zip, path, `${folderPath}/${item.documentName}`);
              if (success) {
                stats.successfulFiles++;
                fileAdded = true;
                console.log(`Successfully added file from: ${path}`);
                break;
              }
            }
            
            if (!fileAdded) {
              stats.failedFiles++;
              console.warn(`Failed to find file: ${item.documentName}`);
            }
          }
        }
      } else {
        // Handle certificates without certificateID (UNKNOWN status)
        console.log(`Certificate without ID: ${item.documentName}`);
        if (item.documentName) {
          // Try common paths for certificates without IDs
          const pathsToTry = [
            `protected/file-cabinet/${clientID}/${folder.id}/${item.documentName}`,
            `protected/certificates/${folder.id}/${item.documentName}`,
            `protected/certificates/${clientID}/${item.documentName}`
          ];
          
          stats.totalFiles++;
          let fileAdded = false;
          
          for (const path of pathsToTry) {
            console.log(`Trying path for certificate without ID: ${path}`);
            const success = await addFileToZip(zip, path, `${folderPath}/${item.documentName}`);
            if (success) {
              stats.successfulFiles++;
              fileAdded = true;
              console.log(`Successfully added certificate without ID from: ${path}`);
              break;
            }
          }
          
          if (!fileAdded) {
            stats.failedFiles++;
            console.warn(`Failed to find certificate without ID: ${item.documentName}`);
          }
        }
      }
      } else {
        // Handle any other file types we haven't considered
        console.warn(`Unknown item type: ${item.documentType} for file: ${item.documentName}`);
      }
    }
  }
  
  // Recursively add subfolders
  if (folderContents.subFolders && folderContents.subFolders.items) {
    for (const subFolder of folderContents.subFolders.items) {
      const subStats = await addFolderToZip(zip, subFolder, folderPath, clientID);
      stats.totalFiles += subStats.totalFiles;
      stats.successfulFiles += subStats.successfulFiles;
      stats.failedFiles += subStats.failedFiles;
    }
  }
  
  // Log summary for this folder
  console.log(`Folder ${folder.name} processed: ${stats.successfulFiles}/${stats.totalFiles} files added successfully`);
  if (stats.failedFiles > 0) {
    console.warn(`Failed to add ${stats.failedFiles} files from folder ${folder.name}`);
  }
  
  return stats;
}

/**
 * Creates a zip file of the folder and its contents and triggers download
 */
export async function zipAndDownloadFolder(folder, clientID, onProgress) {
  try {
    console.log('Starting zip download for folder:', folder);
    console.log('Client ID:', clientID);
    
    // Update progress
    if (onProgress) {
      onProgress({ status: 'starting', message: 'Preparing to create zip file...' });
    }
    
    const zip = new JSZip();
    
    // Add folder contents to zip
    if (onProgress) {
      onProgress({ status: 'processing', message: `Processing folder: ${folder.name}` });
    }
    
    const stats = await addFolderToZip(zip, folder, '', clientID);
    
    // Log final statistics
    console.log(`=== ZIP DOWNLOAD SUMMARY ===`);
    console.log(`Total files processed: ${stats.totalFiles}`);
    console.log(`Successfully added: ${stats.successfulFiles}`);
    console.log(`Failed to add: ${stats.failedFiles}`);
    console.log(`===========================`);
    
    // Warn if no files were added
    if (stats.successfulFiles === 0) {
      const message = stats.totalFiles === 0 
        ? 'No files found in the selected folder' 
        : 'Failed to download any files from the folder';
      console.error(message);
      if (onProgress) {
        onProgress({ 
          status: 'error', 
          message: message 
        });
      }
      throw new Error(message);
    }
    
    // Generate the zip file
    if (onProgress) {
      onProgress({ status: 'generating', message: 'Generating zip file...' });
    }
    
    const content = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 6
      }
    }, (metadata) => {
      if (onProgress && metadata.percent) {
        onProgress({ 
          status: 'compressing', 
          message: `Compressing: ${Math.round(metadata.percent)}%`,
          percent: metadata.percent
        });
      }
    });
    
    // Create download link and trigger download
    const url = URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${folder.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().getTime()}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL
    setTimeout(() => URL.revokeObjectURL(url), 100);
    
    if (onProgress) {
      onProgress({ status: 'complete', message: 'Download started!' });
    }
    
    return true;
  } catch (error) {
    console.error('Error creating zip file:', error);
    if (onProgress) {
      onProgress({ 
        status: 'error', 
        message: `Failed to create zip: ${error.message}` 
      });
    }
    throw error;
  }
}